import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import type { Renderer2 } from '@angular/core';
import {
	DOCUMENT,
	inject,
	Injectable,
	InjectionToken,
	PLATFORM_ID,
	RendererFactory2,
} from '@angular/core';
import type { MetaDefinition } from '@angular/platform-browser';
import { Meta, Title } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';

import type { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import { languages } from '@valk-nx/core/lib/core';
import type {
	ContentHotelInterface,
	HotelMainContactInterface,
} from '@valk-nx/core/lib/interfaces/hotels.interface';
import type { PhotoInterface } from '@valk-nx/core/lib/interfaces/image.interface';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { removeTrailingSlash } from '@valk-nx/storyblok-helpers/src/lib/general/sitemap.helper';
import type {
	MiniHotelSiteData,
	PackageData,
	RoomData,
} from '@valk-nx/storyblok-types/src/lib/types/content.interface';
import type {
	FilterableHotel,
	FilterablePackageBatch,
} from '@valk-nx/storyblok-types/src/lib/types/filterable-list.service.interface';
import type {
	Link,
	PageMetaData,
} from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

import {
	acceptedPaymentMethodSchemeOrg,
	amenitySchemeOrg,
	brandSchemeOrg,
	businessFunctionSchemeOrg,
	collectionPageSchemeOrg,
	contactPointSchemeOrg,
	eligibleCustomerTypeSchemeOrg,
	filterableHotelAddressSchemaOrg,
	filterableHotelRatingSchemaOrg,
	imageSchemeOrg,
	mainEntityOfPageSchemeOrg,
	offersSchemeOrg,
	organizationSchemeOrg,
	parentOrganizationSchemeOrg,
	postalAddressSchemeOrg,
	priceSpecificationSchemeOrg,
	quantitativeValueSchemeOrg,
	socialProfilesSchemeOrg,
} from './metadata.helper';

export interface MetaDataBatchInterface {
	meta?: {
		description: string;
		title: string;
	};
	images: PhotoInterface[];
}

export const HOST: InjectionToken<string> = new InjectionToken<string>('host');

export const WEBSITE_NAME: InjectionToken<string | null> = new InjectionToken<
	string | null
>('website-name');

const HOMEPAGE_URLS = new Set([
	'/',
	...languages.map((language) => `/${language}`),
]);

// Nominal display sizes declared on JSON-LD ImageObjects; Bynder transform
// URLs carry no intrinsic dimensions, so schemas declare the size at which
// the page renders the image.
const CAROUSEL_IMAGE_DIMENSIONS = { width: 714, height: 513 };
const PACKAGE_CARD_IMAGE_DIMENSIONS = { width: 283, height: 159 };

@Injectable({ providedIn: 'root' })
export class MetadataService {
	private readonly metaTagService = inject(Meta);
	private readonly titleService = inject(Title);
	private readonly translate = inject(TranslateService);
	private readonly platformId = inject(PLATFORM_ID);
	readonly hostUrl = inject(HOST, { optional: true });
	private readonly websiteName = inject(WEBSITE_NAME, { optional: true });
	private readonly router = inject(Router);
	private readonly document = inject<Document>(DOCUMENT);
	// Renderer2 is not injectable in services; create one via the factory.
	private readonly renderer2 = inject(RendererFactory2).createRenderer(
		null,
		null,
	);
	private readonly BRAND_URL = 'https://www.valk.com#VDV';

	constructor() {
		if (isPlatformBrowser(this.platformId) && !this.hostUrl) {
			this.hostUrl = `${window.location.protocol}//${window.location.host}`;
		}
	}

	updateMetadata(metadata: PageMetaData): void {
		const metatags: MetaDefinition[] =
			this.generateMetaDefinitions(metadata);

		this.removeMetaDefinitions();
		this.metaTagService.addTags([
			...metatags,
			{
				property: 'og:url',
				content: `${this.hostUrl}${this.router.url}`,
			},
			{
				name: 'robots',
				content: this.generateRobots(
					metadata.robotsNoIndex,
					metadata.robotsNoFollow,
				),
			},
		]);

		this.titleService.setTitle(metadata.metaTitle);
	}

	removeMetaDefinitions(): void {
		this.metaTagService.removeTag("name='title'");
		this.metaTagService.removeTag("property='og:title'");
		this.metaTagService.removeTag("name='description'");
		this.metaTagService.removeTag("property='og:description'");
		this.metaTagService.removeTag("property='og:type'");
		this.metaTagService.removeTag("property='og:url'");
		this.metaTagService.removeTag("name='robots'");
	}

	generateMetaDefinitions(metadata: PageMetaData): MetaDefinition[] {
		return [
			{ name: 'title', content: metadata.metaTitle },
			{ property: 'og:title', content: metadata.metaTitle },
			{ name: 'description', content: metadata.metaDescription },
			{
				property: 'og:description',
				content: metadata.metaDescription,
			},
			{ property: 'og:type', content: 'website' },
		];
	}

	generateRobots(noIndex: boolean, noFollow: boolean): string {
		let robots = noIndex ? 'noindex' : 'index';
		robots = noFollow ? `${robots}, nofollow` : `${robots}, follow`;
		return robots;
	}

	createLinkForCanonicalURL(canonical: Link | null) {
		let href = `${this.hostUrl}${this.router.url}`;

		if (canonical?.cached_url?.startsWith('http')) {
			href = canonical.cached_url;
		} else if (canonical?.cached_url && canonical?.id !== '') {
			const normalized = canonical.cached_url.startsWith('/')
				? canonical.cached_url
				: `/${canonical.cached_url}`;
			const route = normalized.replace(/\/home$/, '');
			href = `${this.hostUrl}${route}`;
		}

		const existingLink: HTMLLinkElement | null =
			this.document.querySelector('link[rel="canonical"]');

		const link: HTMLLinkElement =
			existingLink || this.document.createElement('link');

		link.setAttribute('rel', 'canonical');

		this.document.head.appendChild(link);
		link.setAttribute('href', removeTrailingSlash(href));
	}

	setHrefLang(translatedSlugs: TranslatedSlug[], defaultLanguage: string) {
		const defaultSlug = {
			path: '',
			...translatedSlugs.find(
				(slug) =>
					slug.lang.toLowerCase() === defaultLanguage.toLowerCase(),
			),
			lang: 'x-default',
		};

		[...translatedSlugs, defaultSlug].forEach((translatedSlug) => {
			const path = translatedSlug.path.endsWith('home')
				? translatedSlug.path.replace(/\/?home$/, '')
				: translatedSlug.path;

			const headDomElements = Array.from(
				this.document.head.children,
			) as unknown as HTMLLinkElement[];
			const alternateLink = headDomElements.find(
				(child: HTMLLinkElement) =>
					child.rel == 'alternate' &&
					child.hreflang.toLowerCase() ==
						translatedSlug.lang.toLowerCase(),
			);

			const href = removeTrailingSlash(`${this.hostUrl}/${path}`);

			if (alternateLink) {
				alternateLink.setAttribute('href', href);
			} else {
				const link = this.document.createElement('link');
				link.setAttribute('rel', 'alternate');
				this.document.head.appendChild(link);
				link.setAttribute(
					'hreflang',
					translatedSlug.lang.toLowerCase(),
				);
				link.setAttribute('href', href);
			}
		});
	}

	updateMetaDataBatch(
		data: MetaDataBatchInterface,
		robotsNoFollow = false,
		robotsNoIndex = false,
	) {
		this.updateMetadata({
			metaDescription: data.meta?.description ?? '',
			metaTitle: data.meta?.title ?? '',
			robotsNoFollow,
			robotsNoIndex,
		});
	}

	/**
	 * Publishes the configured website name as `og:site_name` and, on the
	 * homepage, as a WebSite schema. Sites without a configured name stay silent
	 * rather than offering Google a derived name it might adopt as the site name.
	 */
	setSiteName(): void {
		if (!this.websiteName) {
			return;
		}

		this.metaTagService.updateTag({
			property: 'og:site_name',
			content: this.websiteName,
		});

		if (this.hostUrl && this.isHomepageOnServer()) {
			this.appendSchemaScript(this.renderer2, {
				'@context': 'https://schema.org',
				'@type': 'WebSite',
				url: `${this.hostUrl}/`,
				name: this.websiteName,
			});
		}
	}

	setDefaultSchema(renderer2: Renderer2) {
		if (!this.hostUrl) {
			return;
		}
		const domainName = new URL(this.hostUrl).hostname
			.replace(/^www\./, '')
			.split('.')
			.slice(0, -1)
			.join('.');

		if (this.isHomepageOnServer()) {
			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['Organization'],
				'@id': `${this.hostUrl}/#Organization`,
				name: this.websiteName || domainName,
				url: this.hostUrl,
				logo: 'https://www.valkdeals.nl/assets/logos/vdv-logo.svg',
				parentOrganization: {
					'@type': ['Brand', 'Organization'],
					'@id': 'https://www.valk.com#VDV',
					name: 'Van der Valk',
					url: 'https://www.valk.com',
					sameAs: [
						'https://g.co/kgs/kJaxLh',
						'https://www.wikidata.org/wiki/Q2802214',
						'https://nl.wikipedia.org/wiki/Van_der_Valk_(bedrijf)',
						'https://www.instagram.com/valkverrast',
						'https://www.facebook.com/VanderValkVerrast',
						'https://www.youtube.com/user/HotelsVanDerValk',
						'https://www.tiktok.com/@valkverrast',
						'https://nl.pinterest.com/vandervalkhotels/',
					],
				},
				foundingDate: '1939',
				contactPoint: {
					'@type': 'ContactPoint',
					telephone: '088 - 024 6301',
					contactType: 'Customer Service',
					hoursAvailable: {
						'@type': 'OpeningHoursSpecification',
						opens: '9:00',
						closes: '17:00',
						dayOfWeek: [
							'Monday',
							'Tuesday',
							'Wednesday',
							'Thursday',
							'Friday',
						],
					},
				},
				address: {
					'@type': 'PostalAddress',
					streetAddress: 'Stationsweg 91',
					addressLocality: 'Breukelen',
					postalCode: '3621 LK',
					addressCountry: 'NL',
				},
				email: 'support@valkservices.nl',
				brand: {
					'@id': 'https://www.valk.com/#VDV',
				},
			});
		}
	}

	setSchemaOrgHotel(renderer2: Renderer2, hotelData: ContentHotelInterface) {
		if (this.isHomepageOnServer()) {
			const contact = hotelData.contacts.find(
				(contact) => contact.type === 'main',
			);

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['Organization', 'LocalBusiness', 'Hotel'],
				'@id': `${this.hostUrl}/#Organization`,
				name: this.websiteName || hotelData.name,
				url: this.hostUrl,
				logo: `${this.hostUrl}/assets/images/application-icons/icon-512x512.png`,
				openingHours: 'Mo, Tu, We, Th, Fr, Sa, Su 00:00 - 23:59',
				parentOrganization: parentOrganizationSchemeOrg(this.BRAND_URL),
				contactPoint: contactPointSchemeOrg(contact?.phone),
				address: postalAddressSchemeOrg(hotelData.address),
				email: contact?.email,
				telephone: contact?.phone,
				brand: brandSchemeOrg(this.BRAND_URL),
			});
		}
	}

	setSchemaOrgMiniHotel(renderer2: Renderer2, hotelData: MiniHotelSiteData) {
		if (!isPlatformServer(this.platformId)) {
			return;
		}

		const url = this.getCurrentUrl();
		const {
			socials,
			rating,
			address,
			contact,
			description,
			numberOfRooms,
		} = hotelData;

		this.appendSchemaScript(renderer2, {
			'@context': 'https://schema.org',
			'@type': ['Hotel', 'LocalBusiness'],
			'@id': `${url}#Hotel`,
			name: hotelData.name,
			url,
			...(description && {
				description: GeneralHelper.fromHtmlToString(description),
			}),
			...imageSchemeOrg(hotelData.images, 800, 600, true),
			parentOrganization: parentOrganizationSchemeOrg(this.BRAND_URL),
			contactPoint: contactPointSchemeOrg(contact.phone),
			address: postalAddressSchemeOrg(address),
			geo: {
				'@type': 'GeoCoordinates',
				latitude: address.latitude,
				longitude: address.longitude,
			},
			telephone: contact.phone,
			email: contact.email,
			checkinTime: this.formatHotelTime(hotelData.checkInTime),
			checkoutTime: this.formatHotelTime(hotelData.checkOutTime),
			...(numberOfRooms !== undefined && { numberOfRooms }),
			...(rating.stars && {
				starRating: {
					'@type': 'Rating',
					ratingValue: rating.stars,
				},
			}),
			...(rating.numberOfRatings &&
				rating.score && {
					aggregateRating: {
						'@type': 'AggregateRating',
						ratingValue: rating.score,
						reviewCount: rating.numberOfRatings,
						bestRating: 10,
					},
				}),
			...socialProfilesSchemeOrg(socials),
			acceptedPaymentMethod: acceptedPaymentMethodSchemeOrg(),
			brand: brandSchemeOrg(this.BRAND_URL),
		});
	}

	setSchemaOrgHotelList(
		name: string,
		description: string,
		hotels: FilterableHotel[],
	) {
		if (!isPlatformServer(this.platformId) || hotels.length === 0) {
			return;
		}

		const url = this.getCurrentUrl();
		const brand = this.BRAND_URL;

		this.appendSchemaScript(this.renderer2, {
			'@context': 'https://schema.org',
			'@type': 'ItemList',
			'@id': `${url}#ItemList`,
			name,
			url,
			...(description && { description }),
			numberOfItems: hotels.length,
			itemListElement: hotels.map((hotel, index) =>
				this.buildFilterableHotelListItem(hotel, index, url, brand),
			),
			mainEntityOfPage: this.buildListCollectionPage(
				name,
				description,
				url,
				brand,
			),
		});
	}

	private buildListCollectionPage(
		name: string,
		description: string,
		url: string,
		brand: string,
		mentionsUrl: string | null = null,
	) {
		const inLanguage = this.getCurrentLanguage();

		return {
			'@type': 'CollectionPage',
			'@id': url,
			url,
			name,
			...(description && { description }),
			publisher: { '@id': brand },
			...(mentionsUrl && { mentions: { '@id': mentionsUrl } }),
			...(inLanguage && { inLanguage }),
		};
	}

	private buildFilterableHotelListItem(
		hotel: FilterableHotel,
		index: number,
		listUrl: string,
		brand: string,
	) {
		const hotelId = hotel.slug || hotel.guid;
		const hotelUrl = hotel.slug ? `${listUrl}/${hotel.slug}` : listUrl;
		const description = this.filterableHotelDescription(hotel.content);

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@type': 'Hotel',
				'@id': `${listUrl}/${hotelId}#Hotel`,
				name: hotel.name,
				url: hotelUrl,
				...(description && { description }),
				...imageSchemeOrg(hotel.images, 800, 600),
				address: filterableHotelAddressSchemaOrg(hotel.address),
				geo: {
					'@type': 'GeoCoordinates',
					latitude: hotel.address.latitude,
					longitude: hotel.address.longitude,
				},
				...filterableHotelRatingSchemaOrg(hotel.rating),
				brand: { '@type': 'Brand', '@id': brand },
			},
		};
	}

	private filterableHotelDescription(
		content: FilterableHotel['content'],
	): string {
		const raw =
			content.type === 'array'
				? content.value.join(' ')
				: (content.value as string);
		return GeneralHelper.fromHtmlToString(raw);
	}

	setSchemaOrgFilterablePackageList(
		name: string,
		description: string,
		images: PhotoInterface[],
		packageBatches: FilterablePackageBatch[],
	) {
		if (!isPlatformServer(this.platformId) || packageBatches.length === 0) {
			return;
		}

		const url = this.getCurrentUrl();
		const brand = this.BRAND_URL;

		this.appendSchemaScript(this.renderer2, {
			'@context': 'https://schema.org',
			'@type': 'ItemList',
			'@id': `${url}#ItemList`,
			name,
			url,
			...(description && { description }),
			...imageSchemeOrg(
				images,
				CAROUSEL_IMAGE_DIMENSIONS.width,
				CAROUSEL_IMAGE_DIMENSIONS.height,
				true,
			),
			numberOfItems: packageBatches.length,
			itemListElement: packageBatches.map((batch, index) =>
				this.buildFilterablePackageListItem(batch, index, brand),
			),
			mainEntityOfPage: this.buildListCollectionPage(
				name,
				description,
				url,
				brand,
				this.getParentPageUrl(),
			),
		});
	}

	private buildFilterablePackageListItem(
		batch: FilterablePackageBatch,
		index: number,
		brand: string,
	) {
		const name = batch.meta.title || batch.name;
		const description = GeneralHelper.fromHtmlToString(
			batch.meta.description || batch.description,
		);
		const images = batch.images.map((image) => ({
			...image,
			alt: image.alt || batch.hotelName,
		}));

		return {
			'@type': 'ListItem',
			position: index + 1,
			item: {
				'@type': 'Offer',
				'@id': `${batch.toUrl}#Arrangement`,
				name,
				...(description && { description }),
				url: batch.toUrl,
				...imageSchemeOrg(
					images,
					PACKAGE_CARD_IMAGE_DIMENSIONS.width,
					PACKAGE_CARD_IMAGE_DIMENSIONS.height,
				),
				offeredBy: { '@id': brand },
				seller: { '@id': brand },
				...businessFunctionSchemeOrg('Sell'),
				...eligibleCustomerTypeSchemeOrg(),
				...priceSpecificationSchemeOrg(batch.price, 'IE'),
				potentialAction: {
					'@type': 'ReserveAction',
					target: batch.toUrl,
				},
				mainEntityOfPage: mainEntityOfPageSchemeOrg(
					batch.toUrl,
					name,
					brand,
				),
			},
		};
	}

	/** URL of the parent page (current URL minus its last path segment). */
	private getParentPageUrl(): string | null {
		const [path] = this.router.url.split('?');
		const segments = path.split('/').filter(Boolean);

		return segments.length > 1
			? `${this.hostUrl}/${segments.slice(0, -1).join('/')}`
			: null;
	}

	setSchemaOrgRoomList(
		renderer2: Renderer2,
		name: string,
		roomData: RoomData[],
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'ItemList',
				'@id': `${url}#LP`,
				name,
				url,
				numberOfItems: roomData.length,
				itemListElement: roomData.map((room, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					item: {
						'@type': ['HotelRoom', 'Product'],
						'@id': `${url}/${room.slug}#Room`,
						name: room.title,
						description: GeneralHelper.fromHtmlToString(
							room.description,
						),
						url: `${url}/${room.slug}`,
						containedInPlace: { '@id': url + '#Hotel' },
						...imageSchemeOrg(room.images, 256, 217),
						...amenitySchemeOrg(room.facilities),
						...offersSchemeOrg(room.price, brand),
						potentialAction: {
							'@type': 'ReserveAction',
							target: room.bookingToolRedirectUrl,
						},
						brand: brandSchemeOrg(brand),
						mainEntityOfPage: mainEntityOfPageSchemeOrg(
							`${url}/${room.slug}`,
							room.title,
							brand,
						),
					},
				})),
				mainEntityOfPage: mainEntityOfPageSchemeOrg(
					url,
					name,
					brand,
					inLanguage,
					`${url}#LP`,
				),
			});
		}
	}
	setSchemaOrgRoomDetail(
		renderer2: Renderer2,
		room: RoomData,
		_hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': ['HotelRoom', 'Product'],
				'@id': `${url}#Room`,
				name: room.title,
				description: GeneralHelper.fromHtmlToString(room.description),
				url,
				accommodationCategory: 'Hotel',
				category: 'Hotelkamer',
				...imageSchemeOrg(room.images, 800, 640, true),
				...amenitySchemeOrg(room.facilities),
				...offersSchemeOrg(room.price, brand),
				potentialAction: {
					'@type': 'ReserveAction',
				},
				floorSize: quantitativeValueSchemeOrg(
					room.surfaceArea / 10000,
					'MTK',
				),
				bed: {
					'@type': 'BedDetails',
					typeOfBed: room.facilities.sleepComfort,
				},
				occupancy: quantitativeValueSchemeOrg(
					room.occupancyRestrictions.maxPersons || 0,
					'C62',
				),
				brand: brandSchemeOrg(brand),
				mainEntityOfPage: mainEntityOfPageSchemeOrg(
					url,
					room.title,
					brand,
					inLanguage,
					`${url}#Room`,
				),
			});
		}
	}

	setSchemaOrgFaq(
		renderer2: Renderer2,
		name: string,
		description: string,
		faqs: { question: string; answer: string }[],
	) {
		if (isPlatformServer(this.platformId) && faqs.length > 0) {
			const url = this.getCurrentUrl();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'FAQPage',
				'@id': `${url}#FAQ`,
				url,
				name,
				description,
				mainEntity: faqs.map((faq) => ({
					'@type': 'Question',
					name: faq.question,
					acceptedAnswer: {
						'@type': 'Answer',
						text: faq.answer,
					},
				})),
				publisher: {
					'@type': 'Organization',
					'@id': `${this.hostUrl}/#Organization`,
				},
			});
		}
	}

	setSchemaOrgPackageList(
		renderer2: Renderer2,
		name: string,
		description: string,
		images: PhotoInterface[],
		packageData: PackageData[],
		_hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'ItemList',
				'@id': `${url}#LP`,
				name,
				url,
				...(description && { description }),
				...imageSchemeOrg(
					images,
					CAROUSEL_IMAGE_DIMENSIONS.width,
					CAROUSEL_IMAGE_DIMENSIONS.height,
					true,
				),
				numberOfItems: packageData.length,
				itemListElement: packageData.map((pkg, index) => ({
					'@type': 'ListItem',
					position: index + 1,
					item: {
						'@type': 'Offer',
						'@id': `${url}/${pkg.slug}#Package`,
						name: pkg.name,
						description: GeneralHelper.fromHtmlToString(
							pkg.description,
						),
						url: `${url}/${pkg.slug}`,
						...imageSchemeOrg(
							pkg.images,
							PACKAGE_CARD_IMAGE_DIMENSIONS.width,
							PACKAGE_CARD_IMAGE_DIMENSIONS.height,
						),
						offeredBy: { '@id': brand },
						seller: { '@id': brand },
						...businessFunctionSchemeOrg('Sell'),
						...eligibleCustomerTypeSchemeOrg(),
						...priceSpecificationSchemeOrg(pkg.price, 'IE'),
						potentialAction: {
							'@type': 'ReserveAction',
							target: `${url}/${pkg.slug}`,
						},
						mainEntityOfPage: mainEntityOfPageSchemeOrg(
							`${url}/${pkg.slug}`,
							pkg.name,
							brand,
							inLanguage,
							`${url}/${pkg.slug}`,
						),
					},
				})),
				mainEntityOfPage: collectionPageSchemeOrg(
					url,
					this.titleService.getTitle(),
					brand,
					`${this.hostUrl}/#Hotel`,
					inLanguage,
				),
			});
		}
	}

	setSchemaOrgPackageDetail(
		renderer2: Renderer2,
		pkg: PackageData,
		hotel: HotelMainContactInterface,
	) {
		if (isPlatformServer(this.platformId)) {
			const brand = this.BRAND_URL;
			const url = this.getCurrentUrl();
			const inLanguage = this.getCurrentLanguage();

			this.appendSchemaScript(renderer2, {
				'@context': 'https://schema.org',
				'@type': 'Offer',
				'@id': `${url}#Package`,
				name: pkg.name,
				description: GeneralHelper.fromHtmlToString(pkg.description),
				url,
				availability: pkg.isExpired
					? 'https://schema.org/OutOfStock'
					: 'https://schema.org/InStock',
				validFrom: pkg.bookable.from,
				validThrough: pkg.bookable.to,
				...imageSchemeOrg(pkg.images, 714, 513, true),
				itemOffered: {
					'@type': 'Service',
					name: pkg.name,
					description: GeneralHelper.fromHtmlToString(
						pkg.description,
					),
				},
				offeredBy: organizationSchemeOrg('Van der Valk', brand),
				seller: organizationSchemeOrg('Van der Valk', brand),
				...businessFunctionSchemeOrg('LeaseOut'),
				...eligibleCustomerTypeSchemeOrg(),
				sku: pkg.slug,
				...priceSpecificationSchemeOrg(pkg.price, 'C62'),
				acceptedPaymentMethod: acceptedPaymentMethodSchemeOrg(),
				potentialAction: {
					'@type': 'ReserveAction',
					target: url,
				},
				mainEntityOfPage: {
					...mainEntityOfPageSchemeOrg(
						`${url}`,
						`${pkg.name} | Van der Valk ${hotel.name}`,
						`${brand}#Organization`,
						inLanguage,
					),
				},
			});
		}
	}

	private appendSchemaScript(
		renderer2: Renderer2,
		schemaObject: object,
	): void {
		const script = renderer2.createElement('script');
		script.type = `application/ld+json`;
		script.text = JSON.stringify(schemaObject);
		renderer2.appendChild(this.document.head, script);
	}

	private isHomepageOnServer(): boolean {
		return (
			isPlatformServer(this.platformId) &&
			HOMEPAGE_URLS.has(this.router.url)
		);
	}

	private getCurrentLanguage(): string | null {
		return (
			this.translate.getCurrentLang() || this.translate.getFallbackLang()
		);
	}

	private getCurrentUrl(): string {
		return `${this.hostUrl}${this.router.url}`;
	}

	private formatHotelTime(value: number): string {
		const padded = value.toString().padStart(4, '0');
		return `${padded.slice(0, 2)}:${padded.slice(2, 4)}:00`;
	}
}
